// Endereço, contato e navegação a partir do cadastro da unidade consumidora.
//
// Existe porque a regra de endereço da UC estava espalhada: `composeEnderecoUC`
// em views/Financeiro/UCSearchPicker.tsx monta o texto, `urlNavegacao` em
// views/CRM/Rotas/rotaTypes.ts monta o link do Maps mas só sabe partir de uma
// `negociacao`, e a limpeza de logradouro sujo vive de novo dentro da edge
// geocode-leads. A rota de cobrança precisa das três coisas partindo da UC — o
// inadimplente normalmente não tem negociação aberta (medido em 10/09/2026:
// só 182 das 866 UCs com fatura vencida têm).

import { navUrlCoordenada, navUrlEndereco, type AppNavegacao } from './navegacaoMaps.ts';
import { TELEFONE_MIN_DIGITOS } from './validacaoEnvioCobranca.ts';
// A limpeza do endereco da Equatorial ja existe e ja foi MEDIDA: com o
// logradouro cru o Nominatim acertou 0 de 11 enderecos reais; com so o nome da
// rua, 10 de 11. Ela mora em _shared porque a edge geocode-leads usa a mesma —
// mesmo padrao de reexport de src/lib/statusBoleto.ts.
import { prepararConsulta } from '../../supabase/functions/_shared/endereco-geocode.ts';

/** O que se lê do cadastro da UC. Estrutural de propósito: qualquer `select` que
 *  traga estas colunas serve, sem depender do tipo completo de types.ts. */
export interface UcEndereco {
    logradouro?: string | null;
    numero?: string | null;
    complemento?: string | null;
    bairro?: string | null;
    cidade?: string | null;
    estado?: string | null;
    cep?: string | null;
    /** Campo legado: o endereço inteiro num texto só. Último recurso. */
    endereco?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    /** pendente | endereco | rua | bairro | cidade | falhou — ver `urlNavegacaoUc`. */
    geocode_status?: string | null;
    /** Quando a API foi consultada por último. Com 'falhou'/'bairro'/'cidade',
     *  diz que perguntar de novo pelo mesmo endereço só gasta. */
    geocoded_at?: string | null;
    /** O endereço canônico que o geocoder confirmou (`formatted_address`).
     *  É o destino preferido do link de navegação — ver `urlNavegacaoUc`. */
    geocode_label?: string | null;
    whatsapp_cliente?: string | null;
    whatsapp_cliente_2?: string | null;
    /** Quem assina a fatura na Equatorial. Nem sempre é o cliente da Vigor — é o
     *  nome que o cobrador vai ver na porta e na conta de luz. */
    titular_fatura?: string | null;
    /** Os DOIS números que a Equatorial dá à mesma unidade. A fatura vem ora com
     *  um, ora com outro, e é preciso procurar pelos dois — ver `ucCodigoKeys`. */
    numero_uc?: string | null;
    outro_numero_uc?: string | null;
}

/** Endereço do pagador impresso no boleto, extraído da fatura da Equatorial.
 *  São colunas de `faturas` (prefixo `end_`), não da UC — não confundir. */
export interface FaturaEndereco {
    end_logradouro?: string | null;
    end_numero?: string | null;
    end_bairro?: string | null;
    end_cep?: string | null;
    end_cidade?: string | null;
    end_uf?: string | null;
}

const limpo = (v: string | null | undefined): string => String(v ?? '').trim();
const digitos = (v: string | null | undefined): string => String(v ?? '').replace(/\D/g, '');

/**
 * Só o nome da rua, sem a bagagem.
 *
 * O `logradouro` da UC vem da fatura da Equatorial com quadra, lote, "S/N" e
 * complemento grudados — "RUA C-217, Q. 515, L. 14, S/N, CASA - 2" é um valor
 * real do cadastro. Mandar isso inteiro para um geocoder é o que faz ele não
 * achar nada, ou pior, achar outra coisa. Só o trecho antes da primeira vírgula
 * é nome de rua. Mesma regra já aplicada em rotaTypes.ts:74.
 */
export const nomeDaRua = (logradouro: string | null | undefined): string =>
    limpo(logradouro).split(',')[0].trim();

/**
 * O número da porta, ou '' quando o que está lá não é número.
 *
 * Medido no cadastro em 10/09/2026: de 1.802 UCs, 1.282 têm `numero` que não é
 * um número puro — 818 vazias e 422 com alguma grafia de "S/N" (S/N, SN, s/n,
 * "S/N "). "S/N" não localiza nada: aceitar seria mandar o cobrador para o
 * começo da rua achando que chegou no endereço certo.
 *
 * O resto do lixo, porém, é número de verdade com enfeite, e jogar fora seria
 * perder endereço bom: "N. 72" (prefixo), "712-A" (sufixo com hífen), "120A"
 * (sufixo de loteamento) e " 265" (espaço). Esses são recuperados.
 * Já "Sala 1" e "O" são complemento e ruído — continuam fora.
 */
export const numeroDePorta = (numero: string | null | undefined): string => {
    // "N. 72" / "Nº 98" / "No 15" -> "72". Palavra que só começa com N ("NORTE")
    // sobrevive ao corte mas não passa no teste numérico logo abaixo.
    const v = limpo(numero).replace(/^n[.º°o]*\s*/i, '');
    if (!/^\d+\s*-?\s*[A-Za-z]?$/.test(v)) return '';
    // Zero não é porta. O cadastro tem "N. 0" e "N. O" (a letra) vindos do OCR,
    // e mandar "RUA H-139, 0" para o geocoder é pior que mandar só a rua.
    if (Number(v.replace(/\D/g, '')) <= 0) return '';
    return v.replace(/\s+/g, '');
};

/**
 * O número da porta da UC ou, faltando ele, o da fatura.
 *
 * Medido em 10/09/2026: o cadastro da UC só tem número em 313 das 866 UCs
 * inadimplentes, mas a fatura tem em outras 616 — a união cobre 992 de 1.074.
 * É essa união que torna a geocodificação viável; só o cadastro não bastaria.
 */
export const numeroUcOuFatura = (
    uc: UcEndereco | null | undefined,
    fatura?: FaturaEndereco | null,
): string => numeroDePorta(uc?.numero) || numeroDePorta(fatura?.end_numero);

/** CEP com 8 dígitos, ou '' — CEP truncado atrapalha mais do que ajuda. */
export const cepValido = (
    uc: UcEndereco | null | undefined,
    fatura?: FaturaEndereco | null,
): string => {
    // O do cadastro primeiro, mas só se ele disser alguma coisa: o CEP geral do
    // município (74000-000) no cadastro escondia o CEP de rua que a fatura tinha.
    // Caso real: Av. França, Setor Cristina — cadastro 74000-000, fatura
    // 74354-320; a consulta ao geocoder saía sem CEP nenhum.
    const doCadastro = digitos(uc?.cep);
    const daFatura = digitos(fatura?.end_cep);
    const util = (d: string) => d.length === 8 && !cepGenerico(d);
    if (util(doCadastro)) return doCadastro;
    if (util(daFatura)) return daFatura;
    const d = doCadastro || daFatura;
    return d.length === 8 ? d : '';
};

/**
 * O "CEP geral" do município — 74000-000, 75000-000 — que não é de rua nenhuma.
 *
 * Vale a distinção porque ele MENTE para o geocoder: é um CEP de 8 dígitos, passa
 * por `cepValido`, e o Google devolve o centro da cidade com cara de endereço
 * exato. A UC 271752901211 (RUA GUAIAMUM, Q. 106, L. 1) está assim no cadastro.
 * CEP de logradouro de verdade tem os cinco primeiros dígitos diferentes de
 * '000' — 74914-610 passa, 74000-000 não.
 */
export const cepGenerico = (cep: string): boolean =>
    /^\d{2}000000$/.test(digitos(cep));

/** 74914610 -> '74914-610'. Fora do padrão, devolve ''. */
export const cepFormatado = (cep: string | null | undefined): string => {
    const d = digitos(cep);
    return d.length === 8 ? `${d.slice(0, 5)}-${d.slice(5)}` : '';
};

/**
 * Endereço legível, para o cobrador conferir com os próprios olhos.
 *
 * Mantém o logradouro INTEIRO (com quadra e lote), ao contrário da consulta de
 * geocode: para quem está na rua procurando o portão, "Q. 515, L. 14" é
 * justamente a informação que resolve. Cai para o campo legado `endereco`
 * quando não há nada estruturado — é o que `composeEnderecoUC` já fazia.
 *
 * O CEP entra no fim, e é o campo que mais importa em Goiás: em loteamento o
 * endereço é quadra/lote e a casa é "S/N", então o CEP é a única coisa que o
 * Waze e o Maps resolvem sozinhos. O CEP geral do município fica de fora — ver
 * `cepGenerico`, ele levaria o cobrador ao centro da cidade.
 */
export const enderecoUcTexto = (
    uc: UcEndereco | null | undefined,
    fatura?: FaturaEndereco | null,
): string => {
    if (!uc && !fatura) return '';
    const logradouro = limpo(uc?.logradouro) || limpo(fatura?.end_logradouro);
    const numero = numeroUcOuFatura(uc, fatura);
    const bairro = limpo(uc?.bairro) || limpo(fatura?.end_bairro);
    const cidade = limpo(uc?.cidade) || limpo(fatura?.end_cidade);
    const uf = limpo(uc?.estado) || limpo(fatura?.end_uf);

    const cepBruto = cepValido(uc, fatura);
    const cep = cepBruto && !cepGenerico(cepBruto) ? `CEP ${cepFormatado(cepBruto)}` : '';

    const rua = [logradouro, numero].filter(Boolean).join(', ');
    const cidadeUf = cidade ? (uf ? `${cidade}/${uf}` : cidade) : '';

    // O CEP acompanha o endereço, nunca o substitui: se ele entrasse na conta de
    // "tem endereço estruturado?", uma UC que só tem CEP e o campo legado
    // devolveria "CEP 74914-610" sozinho e perderia o endereço que existia.
    const estruturado = [rua, bairro, cidadeUf].filter(Boolean);
    if (estruturado.length > 0) return [...estruturado, cep].filter(Boolean).join(', ');

    const legado = limpo(uc?.endereco);
    return legado && cep ? `${legado}, ${cep}` : legado;
};

/**
 * A string que vai para o geocoder — limpa, ao contrário da exibida.
 *
 * O trabalho pesado é do `prepararConsulta` de _shared/endereco-geocode.ts, que
 * já sabe isolar o nome da rua no meio de "RUA C-217, Q. 515, L. 14, S/N,
 * CASA - 2", extrair o número que às vezes está escondido dentro do logradouro,
 * descartar "SEM NOME", "Segunda via" e sobras de OCR. Aquilo foi medido contra
 * endereços reais; repetir a regra aqui criaria uma segunda versão pior.
 *
 * O que sobra para esta função é o que é específico da cobrança: o resgate do
 * número pela FATURA (a UC só tem número em 313 das 866 inadimplentes; a fatura
 * cobre outras 616) e o CEP, que a consulta do Nominatim não usava e o Google
 * aproveita bem.
 *
 * Devolve '' quando não sobrou nada em que confiar — chamar a API paga com
 * "GOIÂNIA, Brasil" gasta dinheiro para receber de volta o centro do município.
 */
export const consultaGeocodeUc = (
    uc: UcEndereco | null | undefined,
    fatura?: FaturaEndereco | null,
): string => {
    const base = prepararConsulta({
        logradouro: uc?.logradouro || fatura?.end_logradouro,
        numero: uc?.numero,
        bairro: uc?.bairro || fatura?.end_bairro,
        cidade: uc?.cidade || fatura?.end_cidade,
        estado: uc?.estado || fatura?.end_uf,
        cep: uc?.cep,
    });

    const numero = base.numero || numeroDePorta(fatura?.end_numero);
    // CEP geral do município não entra: ele tem 8 dígitos e passa por qualquer
    // validação, mas aponta para o centro da cidade. Mandá-lo junto de uma rua
    // de loteamento faz o Google preferir o centro e devolver 'endereco' —
    // coordenada errada com selo de confiável, que é o pior resultado possível.
    const cepBruto = cepValido(uc, fatura);
    const cep = cepGenerico(cepBruto) ? '' : cepBruto;

    // Sem rua e sem CEP não há endereço: o que sobraria é a cidade, e o geocoder
    // devolveria o centroide com cara de precisão.
    if (!base.rua && !cep) return '';

    return [
        [base.rua, numero].filter(Boolean).join(', '),
        base.bairro,
        base.cidade,
        base.estado,
        cep,
        'Brasil',
    ].filter(Boolean).join(', ');
};

/**
 * Chave de busca no cache `geocode_enderecos`.
 *
 * Espelha a coluna gerada `consulta_norm` (migration 20260916220000) e existe
 * porque a chave crua é a string que foi ao Google, escrita como estava no
 * cadastro naquele dia. O mesmo endereço foi gravado como "…, Goiania, GO, …" e
 * consultado depois como "…, GOIANIA, GO, …"; a igualdade exata não casava, e o
 * sistema tratava como "sem localização" um endereço que já estava pago e
 * guardado — foi o que aconteceu com a RUA DO PETRÓLEO e a RUA TENERIFE na rota
 * de 16/09/2026.
 *
 * Se mexer aqui, mexa na coluna gerada: as duas têm que produzir o mesmo texto.
 */
export const chaveCacheGeocode = (consulta: string | null | undefined): string =>
    String(consulta || '')
        .normalize('NFD').replace(/[̀-ͯ]/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();

/** Para onde o app de mapa leva: um ponto ou um endereço em texto. */
export type DestinoNavegacao =
    | { tipo: 'coordenada'; lat: number; lng: number }
    | { tipo: 'texto'; texto: string };

/**
 * Link de navegação da UC, ou '' quando não há para onde mandar ninguém.
 * A escolha do destino é de `destinoNavegacaoUc`; aqui só vira link do app.
 */
export const urlNavegacaoUc = (
    uc: UcEndereco | null | undefined,
    fatura?: FaturaEndereco | null,
    /** Google por padrão: é o que funciona em qualquer aparelho quando o link
     *  circula por WhatsApp e não se sabe quem vai abrir. Ver navegacaoMaps.ts. */
    app: AppNavegacao = 'google',
): string => {
    const d = destinoNavegacaoUc(uc, fatura, app);
    if (!d) return '';
    return d.tipo === 'coordenada' ? navUrlCoordenada(d, app) : navUrlEndereco(d.texto, app);
};

/**
 * Para onde mandar o cobrador, ou null quando não há.
 *
 * Existe separado do link porque a rota inteira no Google Maps precisa do
 * destino de CADA parada, não de um link por parada: a parada sem pino confiável
 * entra na rota pelo endereço em texto, o mesmo que o botão "Navegar" do card usa.
 *
 * A coordenada é usada quando ela é do ENDEREÇO, e também quando é da RUA e o
 * endereço não tem número de porta. Nos outros níveis ('bairro', 'cidade',
 * 'pendente', 'falhou') ela é aproximação — no nível 'cidade' é o centro do
 * município — e navegar até lá leva o cobrador ao lugar errado com cara de
 * certeza. O diagnóstico de 20/08/2026 achou 125 clientes plotados no centro do
 * município, 41 deles num raio de 1,3 km em Goiânia, com desvio mediano de
 * 3,5 km. Nesses casos vai o endereço em texto, que o Google resolve na hora e
 * que o cobrador consegue conferir.
 *
 * Mesma decisão de `urlNavegacao` (views/CRM/Rotas/rotaTypes.ts), sem a
 * dependência de `negociacoes`.
 */
export const destinoNavegacaoUc = (
    uc: UcEndereco | null | undefined,
    fatura?: FaturaEndereco | null,
    app: AppNavegacao = 'google',
): DestinoNavegacao | null => {
    if (!uc && !fatura) return null;
    const temCoord = uc?.latitude != null && uc?.longitude != null;

    // Mesma preparação do geocode: o Maps também erra com o logradouro cru.
    const base = prepararConsulta({
        logradouro: uc?.logradouro || fatura?.end_logradouro,
        numero: uc?.numero,
        bairro: uc?.bairro || fatura?.end_bairro,
        cidade: uc?.cidade || fatura?.end_cidade,
        estado: uc?.estado || fatura?.end_uf,
        cep: uc?.cep,
    });
    const numero = base.numero || numeroDePorta(fatura?.end_numero);

    const nivel = uc?.geocode_status;
    const confirmado = nivel === 'endereco' || nivel === 'rua';

    // Pino colocado à mão na conferência ganha de tudo, inclusive do texto: uma
    // pessoa olhou o mapa e disse "é aqui". Todo o raciocínio abaixo existe para
    // decidir em quem confiar quando ninguém confirmou nada — aqui alguém
    // confirmou, e mandar o texto por cima desfaria a correção.
    if (nivel === 'manual' && temCoord) {
        return { tipo: 'coordenada', lat: uc!.latitude!, lng: uc!.longitude! };
    }

    // Primeiro destino: o endereço que o próprio geocoder confirmou. Ele é a
    // forma canônica — com o CEP real da via — e evita os dois jeitos de errar
    // que apareceram em campo no mesmo dia:
    //
    //  - a coordenada crua faz o Maps rotular a parada com o comércio conhecido
    //    mais próximo: a UC 271752901211 (R. do Guaiamum, 441) abria como
    //    "Farmácia DrogaShop Jardim Atlântico", em outra rua;
    //  - o endereço do cadastro carrega o CEP da fatura, que às vezes é de
    //    outra cidade: a UC 415267801208 é de Trindade com CEP 74020-000, do
    //    Setor Central de Goiânia, e o link abria a dezenas de km do cliente.
    //
    // Só vale do nível 'rua' para cima. Em 'bairro' e 'cidade' o label é o nome
    // do bairro ou do município, e aí o texto do cadastro diz mais.
    const label = limpo(uc?.geocode_label);

    // Tendo coordenada confirmada, o texto só ganha num caso: nível 'endereco'
    // no Google. Ali o número da porta faz o Maps cair no mesmo ponto — medido
    // em 6 de 6 paradas, desvio mediano zero — e o texto ainda evita que ele
    // rotule a parada com o comércio vizinho.
    //
    // Em 'rua' não há número (o endereço é quadra/lote, S/N) e o Maps passa a
    // resolver "a avenida inteira", largando o pino em qualquer trecho dela: a
    // Av. São Paulo da UC 355307501202 abriu a 930 m da nossa coordenada e a
    // 800 m do CEP 74914-550, que estava no texto. Fora do Google a coordenada
    // ganha sempre: o Apple ignora o CEP do texto e vira "Rua das Bandeiras,
    // 060" (o CEP virou número de porta).
    if (confirmado && temCoord && label && !(app === 'google' && nivel === 'endereco')) {
        return { tipo: 'coordenada', lat: uc!.latitude!, lng: uc!.longitude! };
    }
    if (confirmado && label) return { tipo: 'texto', texto: label };

    // Sem label (UC geocodificada antes desta coluna existir), a coordenada de
    // 'endereco' dispensa o texto — e a de 'rua' também quando não há número de
    // porta, pelo mesmo motivo do CEP acima. Havendo número, o texto ganha: é
    // ele que faz o app parar na porta, e não no meio da via.
    if (temCoord && (nivel === 'endereco' || (nivel === 'rua' && !numero))) {
        return { tipo: 'coordenada', lat: uc!.latitude!, lng: uc!.longitude! };
    }
    // O CEP entra no link, não só na tela: em loteamento o endereço é quadra/lote
    // e a casa é "S/N", e sem o CEP o app cai no meio da rua — ou na rua de mesmo
    // nome em outro bairro. O CEP geral do município fica fora pelo motivo oposto:
    // ele levaria direto ao centro da cidade.
    const cepBruto = cepValido(uc, fatura);
    const cep = cepGenerico(cepBruto) ? '' : cepFormatado(cepBruto);
    const endereco = [
        [base.rua, numero].filter(Boolean).join(', '),
        base.bairro,
        base.cidade,
        cep,
    ].filter(Boolean).join(', ');
    if (endereco) return { tipo: 'texto', texto: `${endereco}, Brasil` };

    // Sem endereço utilizável, a coordenada aproximada ainda é melhor que nada:
    // chega perto o bastante para o cobrador ligar e perguntar o resto.
    return temCoord ? { tipo: 'coordenada', lat: uc!.latitude!, lng: uc!.longitude! } : null;
};

/**
 * A coordenada serve para ORDENAR a rota?
 *
 * 'endereco' e 'rua' servem; 'bairro' e 'cidade' não. A diferença entre ordenar
 * e navegar é o miolo desta distinção: 'rua' é o ponto médio da via, então erra
 * a porta por algumas centenas de metros — irrelevante para decidir em que ordem
 * visitar, fatal para dizer "é aqui". Exigir 'endereco' aqui deixaria quase toda
 * rota sem ordenação, porque em loteamento de Goiás a casa é "S/N" e o Google
 * devolve `route`. Para navegação, ver `urlNavegacaoUc`.
 */
export const temCoordenadaConfiavel = (uc: UcEndereco | null | undefined): boolean =>
    uc?.latitude != null && uc?.longitude != null
    && (uc?.geocode_status === 'endereco' || uc?.geocode_status === 'rua'
        // 'manual' é o pino que alguém arrastou na conferência da rota de
        // cobrança (ver corrigirCoordenadaUc). É a coordenada mais confiável que
        // existe no sistema — foi gente olhando o mapa, não a API chutando — e
        // precisa entrar aqui, senão a UC corrigida volta a contar como "sem
        // localização" e é reenviada para a API paga a cada rota montada.
        || uc?.geocode_status === 'manual');

/**
 * Telefones da UC, só dígitos, sem repetição e sem os curtos demais.
 *
 * `whatsapp_cliente_2` existe para o cliente que pediu mais de um destino de
 * cobrança e é ignorado com frequência — aqui os dois entram, na ordem.
 */
export const telefonesUc = (uc: UcEndereco | null | undefined): string[] => {
    const vistos = new Set<string>();
    return [uc?.whatsapp_cliente, uc?.whatsapp_cliente_2]
        .map(digitos)
        .filter(d => {
            if (d.length < TELEFONE_MIN_DIGITOS || vistos.has(d)) return false;
            vistos.add(d);
            return true;
        });
};

/** (62) 99259-5552 — para exibir. Número fora do padrão sai como veio. */
export const formatarTelefoneBr = (valor: string | null | undefined): string => {
    const d = digitos(valor).replace(/^55(?=\d{10,11}$)/, '');
    if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
    if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
    return limpo(valor);
};

/**
 * Número como o WhatsApp aceita no link `wa.me/`: só dígitos, com o 55. O 55 só
 * entra quando falta — o cadastro às vezes já traz. '' quando não há número
 * utilizável. "(62) 99115-3243" vira "5562991153243" (#6279: o perfil grava o
 * WhatsApp formatado e o link da simulação de economia saía quebrado).
 */
export const numeroWhatsappBr = (valor: string | null | undefined): string => {
    const d = digitos(valor);
    if (d.length < TELEFONE_MIN_DIGITOS) return '';
    return d.length >= 12 && d.startsWith('55') ? d : `55${d}`;
};

/** Link do WhatsApp com destino, ou '' quando não há número utilizável. */
export const linkWhatsappUc = (uc: UcEndereco | null | undefined): string => {
    const [primeiro] = telefonesUc(uc);
    const numero = numeroWhatsappBr(primeiro);
    return numero ? `https://wa.me/${numero}` : '';
};
